import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAppDialog } from '@/hooks/useAppDialog';
import { ShiftSheetDraftProvider } from './ShiftSheetDraftContext';
import { ShiftSheetHeader } from './ShiftSheetHeader';
import { ShiftSheetFooter, ShiftAlertLine } from './ShiftSheetFooter';
import { ShiftTimeline } from './ShiftTimeline';
import { ShiftPlanTab } from './ShiftPlanTab';
import { ShiftCoverageTab } from './ShiftCoverageTab';
import { ShiftNotesCard } from './ShiftNotesCard';
import { ShiftAttendanceTab } from './ShiftAttendanceTab';
import { ShiftHistoryTab } from './ShiftHistoryTab';
import { EditShiftDialog } from './EditShiftDialog';   // BS3-001
import { canEditShift } from './shiftEditRules';
import { SilShiftTimeDialog } from './SilShiftTimeDialog';
import { DuplicateShiftDialog } from './DuplicateShiftDialog';   // BS3-002
import { canDuplicateShift } from './shiftDuplicateRules';
import { useStaffAvailabilityForShift } from './StaffAvailabilityHelpers';
import { visibleTabs, accidentalConflicts, isDelivered } from './shiftSheetRules';
import { topAlert } from './shiftAlertLine';
import { liveAssignments } from './sharedDeliveryRules';
import { Home } from 'lucide-react';

/*
 * UC-A — Shift Details sheet (Final PRD §16–18, §21–25, §27–33, §48–50, §52, §55–59). Same route, same card click.
 * Retired from this surface (hidden, not deleted — Phase 12 retirement candidates, callers recorded in
 * docs/upgrade/SCOPE-UC-A-SHIFT-SHEET-v1.0.md): SilRatioSafetyPanel + RatioRulesScopeNote (C1–C3), CalendarFanoutPanel (C5),
 * ShiftParticipantWindowsPanel (C5 — folded into CalendarSharedGroupPanel as read-only evidence), SilBillingReadinessPanel,
 * SplitBillingChainBadge and SegmentBillingPanel (UC-A-6 — billing control leaves the roster; component + API kept for UC-C).
 */
const NONE = [];   // stable empty defaults for the Edit / Duplicate dialogs (a fresh [] each render re-runs their effects)

export function CalendarShiftDrawer({ shift, open, onClose, staff, onUpdate, permissions = [], onShowHouseView, onOpenGroup, participants = NONE, services = NONE }) {
  const { dangerConfirm } = useAppDialog();
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [silTimeOpen, setSilTimeOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [tab, setTab] = useState('plan');
  const [group, setGroup] = useState(null);
  const [meta, setMeta] = useState({ locked: null, correctionPath: '', authorised: null, rows: [] });
  const [sleepoverFlagOn, setSleepoverFlagOn] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/production/feature-flags/mine').then((res) => { const f = res.data?.data || res.data || {}; setSleepoverFlagOn(!!f.sleepover_billing_enabled); }).catch(() => setSleepoverFlagOn(false));
  }, [open]);
  useEffect(() => { setTab('plan'); setGroup(null); setEditOpen(false); setDupOpen(false); setMeta({ locked: null, correctionPath: '', authorised: null, rows: [] }); }, [shift?.id]);

  const onMeta = useCallback((m) => setMeta((prev) => ({ ...prev, ...m })), []);

  // One shared-delivery read (C5): the sheet owns the group; the Coverage panel reloads through it after every action.
  const loadGroup = useCallback(async () => {
    if (!shift?.id || !shift?.participant_id) { setGroup(null); return null; }
    try {
      const r = await api.get('/shared-delivery/groups', { params: { shift_id: shift.id } });
      const g = (r.data?.data || [])[0];
      const full = g ? (await api.get(`/shared-delivery/groups/${g.id}`)).data?.data || null : null;
      setGroup(full);
      return full;
    } catch { setGroup(null); return null; }
  }, [shift?.id, shift?.participant_id]);
  useEffect(() => { if (open) loadGroup(); }, [open, loadGroup]);

  // Workers whose other bookings we check (§24–25): this shift's workers + the group's live workers.
  const workers = useMemo(() => {
    const seen = new Map();
    (shift?.assigned_staff || []).forEach((s) => seen.set(String(s.id), { id: String(s.id), name: s.name }));
    liveAssignments(group).filter((a) => a.worker_id).forEach((a) => seen.set(String(a.worker_id), { id: String(a.worker_id), name: a.worker_name }));
    return Array.from(seen.values());
  }, [shift?.assigned_staff, group]);
  const { statusMap } = useStaffAvailabilityForShift(workers, { date: shift?.date, startTime: shift?.start_time, endTime: shift?.end_time, excludeShiftId: shift?.id });
  const conflicts = useMemo(() => accidentalConflicts(statusMap, workers, group?.member_shift_ids || []), [statusMap, workers, group?.member_shift_ids]);

  if (!shift) return null;
  const canEdit = permissions.includes('roster.update') || permissions.includes('roster.manage');
  const canCorrect = permissions.includes('roster.manage');
  const readOnly = !!meta.locked;
  const tabs = visibleTabs(permissions);
  const alert = topAlert({ shift, conflicts, group, segments: meta.rows });
  const isSilResident = !!shift.house_id || group?.delivery_type === 'SIL_HOUSE';
  const editState = canEditShift(shift, permissions, { locked: meta.locked, group });
  const currentPsiId = ((meta.rows || []).find((r) => r.participant_service_item_id && r.status !== 'DELETED') || {}).participant_service_item_id || '';

  const handleCancel = async () => {
    const result = await dangerConfirm({ title: 'Cancel this shift?', description: 'This marks the shift as cancelled and notifies any assigned staff. Billing is never set here — the cancellation outcome is recorded separately.', confirmLabel: 'Yes, cancel shift', cancelLabel: 'Keep shift', reasonRequired: true, testId: 'cancel-shift-confirm' });
    if (!result) return;
    try { await api.post(`/roster/shifts/${shift.id}/cancel`, { reason: result.reason || 'Cancelled from scheduling board' }); toast.success('Cancelled'); onUpdate(); onClose(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const width = expanded ? 'w-full sm:max-w-none' : 'w-full sm:w-[640px] sm:max-w-[640px]';
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className={`${width} overflow-y-auto p-0 px-6 flex flex-col`} data-testid="shift-drawer" data-expanded={expanded ? 'true' : 'false'}>
        <SheetTitle className="sr-only">Shift details</SheetTitle>
        <SheetDescription className="sr-only">Plan, coverage, attendance and history of this shift</SheetDescription>
        <ShiftSheetDraftProvider>
          <ShiftSheetHeader shift={shift} group={group} locked={meta.locked} agreement={meta.authorised?.agreement || null} expanded={expanded} onToggleExpand={() => setExpanded((v) => !v)}
            onCancel={handleCancel} canCancel={canEdit && !['COMPLETED', 'CANCELLED', 'INVOICED'].includes(shift.status)} onOpenGroup={() => (onOpenGroup ? onOpenGroup(shift) : setTab('coverage'))}
            onRequestCorrection={() => setTab('plan')} canCorrect={canCorrect}
            onEdit={canEdit ? () => (editState.silTimeOnly ? setSilTimeOpen(true) : setEditOpen(true)) : undefined} editState={editState}
            onDuplicate={permissions.includes('roster.create') ? () => setDupOpen(true) : undefined} duplicateState={canDuplicateShift(shift, permissions)} />
          <div className="flex-1 space-y-3 py-3">
            <ShiftAlertLine alert={alert} onAction={(a) => a?.tab && setTab(a.tab)} />
            {/* client#21: shift notes on the Scheduling Board drawer (editable by roster.update / roster.manage) */}
            <ShiftNotesCard shift={shift} onSaved={onUpdate} />
            {isSilResident && (
              <div className="flex items-center justify-between gap-2 rounded-md border bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-700" data-testid="sil-house-note">
                <span className="flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> House staffing is edited on the house view.</span>
                <button type="button" className="text-sky-700 hover:underline" onClick={() => onShowHouseView && onShowHouseView(shift)} data-testid="sil-house-link">By SIL House</button>
              </div>
            )}
            <ShiftTimeline shift={shift} segments={meta.rows} group={group} assignedStaff={shift.assigned_staff} conflicts={conflicts} />
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-4 h-8" data-testid="shift-sheet-tabs">
                {tabs.map((t) => <TabsTrigger key={t.key} value={t.key} className="text-xs h-7" disabled={!t.enabled} title={t.reason || undefined} data-testid={`shift-tab-${t.key}`}>{t.label}</TabsTrigger>)}
              </TabsList>
              {/* Plan and Coverage stay mounted so an open editor (pending row) survives a tab switch; the others load lazily. */}
              <TabsContent value="plan" forceMount className={`mt-3 ${tab === 'plan' ? '' : 'hidden'}`}><ShiftPlanTab shift={shift} permissions={permissions} onUpdate={onUpdate} onMeta={onMeta} readOnly={readOnly && !canCorrect} /></TabsContent>
              <TabsContent value="coverage" forceMount className={`mt-3 ${tab === 'coverage' ? '' : 'hidden'}`}><ShiftCoverageTab shift={shift} staff={staff} permissions={permissions} onUpdate={onUpdate} group={group} onReloadGroup={loadGroup} conflicts={conflicts} readOnly={readOnly} segments={meta.rows} /></TabsContent>
              <TabsContent value="attendance" className="mt-3"><ShiftAttendanceTab shift={shift} permissions={permissions} onUpdate={onUpdate} segments={meta.rows} sleepoverFlagOn={sleepoverFlagOn} readOnly={readOnly && !isDelivered(shift)} /></TabsContent>
              <TabsContent value="history" className="mt-3">{tab === 'history' && <ShiftHistoryTab shift={shift} />}</TabsContent>
            </Tabs>
          </div>
          <ShiftSheetFooter onSaved={onUpdate} />
        </ShiftSheetDraftProvider>
        <EditShiftDialog shift={shift} open={editOpen} onClose={() => setEditOpen(false)} onSaved={onUpdate}
          participants={participants} services={services} staff={staff || NONE} currentPsiId={currentPsiId} segments={meta.rows} />
        <SilShiftTimeDialog shift={shift} open={silTimeOpen} onClose={() => setSilTimeOpen(false)} onSaved={onUpdate} />
        <DuplicateShiftDialog shift={shift} open={dupOpen} onClose={() => setDupOpen(false)} onSaved={onUpdate} segments={meta.rows} />
      </SheetContent>
    </Sheet>
  );
}
